"use client";

import React, { useEffect, useState } from "react";
import { Calendar, User, Clock, ArrowRight, Share2, Tag } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { AspectRatio } from "@/components/ui/aspect-ratio";



function RelatedPostsConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-80 animate-pulse rounded-xl bg-muted/50" data-index={i} />
        ))}
      </div>
    );
  }

  if (error || posts.length === 0) return null;

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
      {posts.map((post, i) => (
        <Card key={post.id} className="flex h-full flex-col overflow-hidden border-border/50 bg-card/50 transition-colors hover:bg-card" data-index={i}>
          <div className="relative aspect-video w-full overflow-hidden bg-muted">
            {post.featuredImage ? (
              <img
                src={post.featuredImage}
                alt={post.title}
                className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-muted text-muted-foreground">
                <span className="text-sm">No Image</span>
              </div>
            )}
          </div>
          <CardHeader className="p-5">
            <div className="mb-2 flex items-center gap-2 text-xs text-muted-foreground">
              {post.date && (
                <span className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {new Date(post.date).toLocaleDateString()}
                </span>
              )}
            </div>
            <CardTitle className="line-clamp-2 text-lg font-semibold leading-tight">
              <Link to={post.link || "#"} className="hover:text-primary transition-colors">
                {post.title}
              </Link>
            </CardTitle>
          </CardHeader>
          <CardContent className="grow px-5 pb-0">
            <CardDescription className="line-clamp-3 text-sm">
              {post.excerpt ? (
                <div dangerouslySetInnerHTML={{ __html: post.excerpt }} />
              ) : (
                "Read this amazing article to learn more about the latest AI tools and trends."
              )}
            </CardDescription>
          </CardContent>
          <CardFooter className="p-5 pt-4">
            <Link to={post.link || "#"} className="w-full">
              <Button variant="outline" className="w-full cursor-pointer justify-between group-hover:border-primary/50">
                Read Article <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}

function RelatedPosts({ currentPostId, categoryIds }: { currentPostId: string; categoryIds: number[] }) {
  
  const relatedArgs: Record<string, any> = {
    post_type: "post",
    posts_per_page: 3,
    post__not_in: [currentPostId],
    orderby: "date",
    order: "DESC",
  };

  if (categoryIds.length > 0) {
    relatedArgs.category__in = categoryIds;
  }

  const wp_query = new WP_Query(relatedArgs);

  return (
    <div className="mt-16 md:mt-24">
      <div className="mb-8 flex items-center justify-between">
        <h3 className="text-2xl font-bold tracking-tight md:text-3xl">Related Articles</h3>
        <Link to="/blog">
          <Button variant="ghost" className="hidden sm:flex">
            View All <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </Link>
      </div>
      <PostsProvider wp_query={wp_query}>
        <RelatedPostsConsumer />
      </PostsProvider>
    </div>
  );
}



function SinglePostConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl space-y-8 px-4 py-12">
        <div className="space-y-4">
          <div className="h-8 w-3/4 animate-pulse rounded-lg bg-muted/50" />
          <div className="h-12 w-full animate-pulse rounded-lg bg-muted/50" />
        </div>
        <div className="aspect-video w-full animate-pulse rounded-xl bg-muted/50" />
        <div className="space-y-4">
          <div className="h-4 w-full animate-pulse rounded bg-muted/50" />
          <div className="h-4 w-full animate-pulse rounded bg-muted/50" />
          <div className="h-4 w-2/3 animate-pulse rounded bg-muted/50" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center text-center">
        <h2 className="text-2xl font-bold text-destructive">Error Loading Post</h2>
        <p className="text-muted-foreground">{error}</p>
        <Button variant="outline" className="mt-4" onClick={() => window.location.reload()}>
          Try Again
        </Button>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center text-center">
        <h2 className="text-2xl font-bold">Post Not Found</h2>
        <p className="text-muted-foreground">The article you are looking for does not exist.</p>
        <Link to="/" className="mt-4">
          <Button>Go Home</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];
  const categoryIds = post.categories?.map((c) => c.id) || [];

  return (
    <article className="container mx-auto max-w-5xl px-4 py-12 md:py-20">
      {}
      <header className="mx-auto mb-10 max-w-4xl text-center md:mb-14">
        <div className="mb-6 flex flex-wrap items-center justify-center gap-3">
          {post.categories && post.categories.length > 0 && (
            <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
              {post.categories[0].name}
            </span>
          )}
          {post.date && (
            <span className="flex items-center text-sm text-muted-foreground">
              <Calendar className="mr-1.5 h-3.5 w-3.5" />
              {new Date(post.date).toLocaleDateString(undefined, {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </span>
          )}
          <span className="flex items-center text-sm text-muted-foreground">
            <Clock className="mr-1.5 h-3.5 w-3.5" />
            5 min read
          </span>
        </div>

        <h1 className="mb-6 text-3xl font-bold leading-tight tracking-tight md:text-5xl lg:text-6xl">
          {post.title}
        </h1>

        {post.excerpt && (
          <div
            className="mx-auto max-w-2xl text-lg text-muted-foreground md:text-xl"
            dangerouslySetInnerHTML={{ __html: post.excerpt }}
          />
        )}

        {}
        <div className="mt-8 flex items-center justify-center gap-4">
          <Avatar className="h-10 w-10 border border-border">
            <AvatarImage src={post.author?.avatar?.["96"] || ""} alt={post.author?.name} />
            <AvatarFallback>
              <User className="h-5 w-5" />
            </AvatarFallback>
          </Avatar>
          <div className="text-left">
            <p className="text-sm font-semibold">{post.author?.name || "AIAffiliate Team"}</p>
            <p className="text-xs text-muted-foreground">Author</p>
          </div>
        </div>
      </header>

      {}
      {post.featuredImage && (
        <div className="mb-12 overflow-hidden rounded-2xl border border-border/50 shadow-2xl md:mb-20">
          <AspectRatio ratio={16 / 9}>
            <img
              src={post.featuredImage}
              alt={post.title}
              className="h-full w-full object-cover"
            />
          </AspectRatio>
        </div>
      )}

      {}
      <div className="mx-auto max-w-3xl">
        <div
          className="prose prose-lg dark:prose-invert max-w-none 
            prose-headings:font-bold prose-headings:tracking-tight prose-headings:text-foreground
            prose-p:leading-relaxed prose-p:text-muted-foreground
            prose-a:text-primary prose-a:no-underline hover:prose-a:underline
            prose-strong:text-foreground
            prose-ul:list-disc prose-ul:pl-6 prose-ul:text-muted-foreground
            prose-ol:list-decimal prose-ol:pl-6 prose-ol:text-muted-foreground
            prose-blockquote:border-l-4 prose-blockquote:border-primary prose-blockquote:bg-muted/30 prose-blockquote:px-6 prose-blockquote:py-2 prose-blockquote:italic
            prose-img:rounded-xl prose-img:shadow-lg
            [&_iframe]:aspect-video [&_iframe]:w-full [&_iframe]:rounded-xl"
          dangerouslySetInnerHTML={{ __html: post.content || "" }}
        />

        {}
        {post.tags && post.tags.length > 0 && (
          <div className="mt-12 flex flex-wrap gap-2 border-t border-border pt-8">
            <Tag className="h-4 w-4 text-muted-foreground" />
            {post.tags.map((tag) => (
              <Link key={tag.id} to={tag.link || "#"}>
                <span className="rounded-md bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary">
                  #{tag.name}
                </span>
              </Link>
            ))}
          </div>
        )}

        {}
        <div className="mt-10 flex flex-col items-center justify-between gap-6 rounded-xl border border-border bg-card p-6 sm:flex-row">
          <div className="text-center sm:text-left">
            <h4 className="font-semibold">Enjoyed this article?</h4>
            <p className="text-sm text-muted-foreground">Share it with your network.</p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" size="sm" className="gap-2">
              <Share2 className="h-4 w-4" /> Share
            </Button>
            <Link to="/contact">
              <Button size="sm">Contact Us</Button>
            </Link>
          </div>
        </div>

        {}
        <div className="mt-16 rounded-2xl bg-muted/30 p-8 md:p-10">
          <div className="flex flex-col items-center gap-6 text-center sm:flex-row sm:text-left">
            <Avatar className="h-20 w-20 border-2 border-background shadow-md md:h-24 md:w-24">
              <AvatarImage src={post.author?.avatar?.["96"] || ""} alt={post.author?.name} />
              <AvatarFallback className="bg-primary/10 text-primary">
                <User className="h-8 w-8" />
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 space-y-2">
              <div className="flex flex-col items-center gap-2 sm:flex-row sm:justify-between">
                <div>
                  <h3 className="text-xl font-bold">{post.author?.name || "AIAffiliate Team"}</h3>
                  <p className="text-sm font-medium text-primary">Content Creator</p>
                </div>
                <Link to={post.author?.link || "#"}>
                  <Button variant="ghost" size="sm" className="h-8 text-xs">
                    View Profile
                  </Button>
                </Link>
              </div>
              <p className="text-muted-foreground">
                Expert in AI tools and automation software. Dedicated to bringing you the most accurate and up-to-date reviews on the latest technology trends.
              </p>
            </div>
          </div>
        </div>
      </div>

      <Separator className="my-16 md:my-24" />

      {}
      <RelatedPosts currentPostId={post.id} categoryIds={categoryIds} />
    </article>
  );
}



export default function SinglePostTemplateSection() {
  
  
  const currentWPQueryParams = (window as any).wvcClient?.getCurrentWPQueryParams() || {
    post_type: "post",
    p: 1, 
  };

  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section id="single-post-template-section" className="bg-background text-foreground">
      <PostsProvider wp_query={wp_query}>
        <SinglePostConsumer />
      </PostsProvider>
    </section>
  );
}